import React, { useState } from 'react';
import { FiUsers, FiCheck, FiPlus, FiChevronDown, FiX } from 'react-icons/fi';
import { toast } from '../../Toast';
import { ContactRow, isNewInDays } from './contactsData';
import ContactsModal from './ContactsModal';

// a segment rule: field · is / is not · value. Only the fields Investi has on a contact can be counted live.
interface Rule { field: string; op: 'is' | 'is not'; value: string }

const FIELDS: Record<string, string[]> = {
    Status: ['Subscribed', 'Unsubscribed'],
    Tag: [],
    'Signed up': ['In the last 30 days', 'In the last 7 days'],
    'Registry match': ['Top 100 holder', 'Top 20 holder', 'Any shareholder'],
};

const matches = (c: ContactRow, r: Rule) => {
    let hit: boolean | null = null;
    if (r.field === 'Status') hit = (r.value === 'Subscribed') === c.subscribed;
    else if (r.field === 'Tag') hit = c.tags.includes(r.value);
    else if (r.field === 'Signed up') hit = isNewInDays(c, r.value.includes('7') ? 7 : 30);
    // registry matching isn't connected, so those rules can't narrow the count
    if (hit === null) return true;
    return r.op === 'is' ? hit : !hit;
};

// New List or Segment (spec 219:9638). Preview only: nothing is created.
const NewListModal: React.FC<{ contacts: ContactRow[]; onClose: () => void }> = ({ contacts, onClose }) => {
    const tags = Array.from(new Set(contacts.flatMap(c => c.tags))).sort();
    const [kind, setKind] = useState<'list' | 'segment'>('segment');
    const [name, setName] = useState('');
    const [rules, setRules] = useState<Rule[]>([{ field: 'Status', op: 'is', value: 'Subscribed' }]);
    const options = (field: string) => field === 'Tag' ? (tags.length ? tags : ['(no tags yet)']) : FIELDS[field];
    const count = contacts.filter(c => rules.every(r => matches(c, r))).length;
    const usesRegistry = rules.some(r => r.field === 'Registry match');

    const setRule = (i: number, patch: Partial<Rule>) => setRules(rs => rs.map((r, j) => {
        if (j !== i) return r;
        const next = { ...r, ...patch };
        // a new field resets the value to that field's first option
        if (patch.field) next.value = options(patch.field)[0];
        return next;
    }));

    const create = () => {
        toast.info(`Preview only: the ${kind} “${name.trim()}” wasn’t created`);
        onClose();
    };

    const select = (value: string, opts: string[], set: (v: string) => void, label: string) =>
        <span className="contacts-select"><select aria-label={label} value={value} onChange={e => set(e.target.value)}>{opts.map(o => <option key={o}>{o}</option>)}</select><FiChevronDown /></span>;

    return <ContactsModal icon={<FiUsers />} title="New List or Segment" sub="A list is a group you add people to. A segment updates itself from rules." onClose={onClose}
        buttons={<button type="button" className="btn-primary" disabled={!name.trim()} onClick={create}><FiCheck /> Create {kind === 'list' ? 'List' : 'Segment'}</button>}>
        <div className="contacts-kind" role="radiogroup" aria-label="List or segment">
            {(['list', 'segment'] as const).map(k => <button key={k} type="button" role="radio" aria-checked={kind === k} className={kind === k ? 'on' : ''} onClick={() => setKind(k)}>
                <span className="radio" />
                <span><strong>{k === 'list' ? 'List' : 'Segment'}</strong><small>{k === 'list' ? 'You choose who’s in it' : 'Fills itself from rules'}</small></span>
            </button>)}
        </div>
        <label className="contacts-field"><span>Name</span><input value={name} placeholder={kind === 'list' ? 'e.g. Site visit, October' : 'e.g. Top 100 shareholders with email'} onChange={e => setName(e.target.value)} autoFocus /></label>
        {kind === 'segment' && <>
            <div className="contacts-rules-label">Rules — include contacts who match all of these</div>
            {rules.map((r, i) => <div key={i} className="contacts-rule">
                {select(r.field, Object.keys(FIELDS), v => setRule(i, { field: v }), 'Field')}
                {select(r.op, ['is', 'is not'], v => setRule(i, { op: v as Rule['op'] }), 'Operator')}
                {select(r.value, options(r.field), v => setRule(i, { value: v }), 'Value')}
                <button type="button" className="rm" aria-label="Remove rule" disabled={rules.length === 1} onClick={() => setRules(rs => rs.filter((_, j) => j !== i))}><FiX /></button>
            </div>)}
            <button type="button" className="contacts-add-rule" onClick={() => setRules(rs => [...rs, { field: 'Tag', op: 'is', value: options('Tag')[0] }])}><FiPlus /> Add another rule</button>
            <div className="contacts-match"><FiUsers /> <span><strong>{count.toLocaleString()} contacts match right now.</strong> The segment updates automatically{usesRegistry ? ' — registry rules aren’t counted until registry matching is connected' : ''}.</span></div>
        </>}
    </ContactsModal>;
};

export default NewListModal;
